import React, { useState, useEffect, ChangeEvent } from 'react';
import { observer } from 'mobx-react';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import PaymentIcon from '@material-ui/icons/Payment';
import MicIcon from '@material-ui/icons/Mic';
import Alert from '@material-ui/lab/Alert';

import authStore from '~/stores/authStore';
import { fetchSpeechBalance, createSpeechTopUp } from '~/api/speechApi';

const useStyles = makeStyles(({ spacing }) =>
    createStyles({
        alignCenter: {
            display: 'flex',
            alignItems: 'center'
        }
    })
);

const langLib = {
    en: {
        title: 'Speech API',
        balance: 'Balance',
        currency: '$',
        minutes: 'min.',
        totalToppedUp: 'Total topped up',
        totalUsed: 'Total used',
        topUp: 'Top up balance',
        amount: 'Amount',
        youWillGet: 'You will receive',
        pay: 'Pay',
        pricePerMinute: 'Rate: $0.003 / min'
    },
    ru: {
        title: 'Speech API',
        balance: 'Баланс',
        currency: '₽',
        minutes: 'мин.',
        totalToppedUp: 'Пополнения',
        totalUsed: 'Использовано',
        topUp: 'Пополнить баланс',
        amount: 'Сумма',
        youWillGet: 'Вы получите',
        pay: 'Оплатить',
        pricePerMinute: 'Тариф: 0.19 ₽ / мин'
    }
} as const;

const PRICE_PER_MINUTE = { en: 0.003, ru: 0.19 };

// Сервер хранит баланс в RUB. Для USD делим на 100.
const RUB_TO_USD = 100;

const toDisplayCurrency = (rubAmount: number, lang: 'en' | 'ru'): number => {
    return lang === 'en' ? rubAmount / RUB_TO_USD : rubAmount;
};

const SpeechBalance = () => {
    const classes = useStyles();
    const { lang } = authStore;
    const lib = langLib[lang];

    const [balance, setBalance] = useState({ balance: 0, totalToppedUp: 0, totalUsed: 0 });
    const [amount, setAmount] = useState<number | ''>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchSpeechBalance()
            .then(data => {
                setBalance({
                    balance: Number(data.balance) || 0,
                    totalToppedUp: Number(data.totalToppedUp) || 0,
                    totalUsed: Number(data.totalUsed) || 0
                });
            })
            .catch(() => {});
    }, []);

    const minutes = amount ? Number((Number(amount) / PRICE_PER_MINUTE[lang]).toFixed(2)) : 0;

    const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val === '') {
            setAmount('');
        } else {
            const num = Number(val);
            if (num >= 0) setAmount(num);
        }
    };

    const handleTopUp = async () => {
        if (!amount || amount <= 0) return;
        setLoading(true);
        setError('');
        try {
            const { redirect_url } = await createSpeechTopUp({
                currency: lang === 'ru' ? 2 : 1,
                amount: Number(amount)
            });
            document.location.href = redirect_url;
        } catch (err: any) {
            setLoading(false);
            setError(err?.message || err);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={6} className={classes.alignCenter}>
                <MicIcon fontSize="large" color="secondary" />
                <Typography variant="h5" style={{ marginLeft: 8 }}>
                    {lib['title']}
                </Typography>
            </Grid>
            <Grid item xs={6}>
                <Typography variant="caption">{lib['pricePerMinute']}</Typography>
            </Grid>

            <Grid item xs={12}>
                <Divider />
            </Grid>

            <Grid item xs={6}>
                <Typography variant="caption">{lib['balance']}</Typography>
            </Grid>
            <Grid item xs={6}>
                <Typography variant="h6" color="secondary">
                    {toDisplayCurrency(balance.balance, lang).toLocaleString('ru')} {lib['currency']}
                </Typography>
            </Grid>

            <Grid item xs={6}>
                <Typography variant="caption">{lib['totalToppedUp']}</Typography>
            </Grid>
            <Grid item xs={6}>
                <Typography>
                    {toDisplayCurrency(balance.totalToppedUp, lang).toLocaleString('ru')} {lib['currency']}
                </Typography>
            </Grid>

            <Grid item xs={6}>
                <Typography variant="caption">{lib['totalUsed']}</Typography>
            </Grid>
            <Grid item xs={6}>
                <Typography>
                    {toDisplayCurrency(balance.totalUsed, lang).toLocaleString('ru')} {lib['currency']}
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <Divider />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                    {lib['topUp']}
                </Typography>
            </Grid>

            {error && (
                <Grid item xs={12}>
                    <Alert severity="error">{error}</Alert>
                </Grid>
            )}

            <Grid item xs={4}>
                <TextField
                    label={lib['amount']}
                    value={amount}
                    onChange={handleAmountChange}
                    type="number"
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{ min: 10, step: 1 }}
                />
            </Grid>
            <Grid item xs={4} className={classes.alignCenter}>
                <Typography variant="body2">
                    {lib['youWillGet']}:{' '}
                    <b>
                        {minutes} {lib['minutes']}
                    </b>
                </Typography>
            </Grid>
            <Grid item xs={4}>
                <Button
                    disabled={loading || !amount || amount <= 0}
                    onClick={handleTopUp}
                    color="secondary"
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    fullWidth
                >
                    {lib['pay']} {amount ? authStore.printCost(Number(amount)) : ''}
                </Button>
            </Grid>
        </Grid>
    );
};

export default observer(SpeechBalance);
